import { makeAutoObservable, runInAction } from 'mobx';
import queryString, { ParsedQuery } from 'query-string';
import { toast } from 'react-toastify';
import { prepareSubscriptionRequest } from '../utils/prepareSubscriptionRequest';
import {
  BNS3_CHARITY_LOT_CATEGORY_ID,
  charityBannerCategoryId,
  lotsPerPage,
  sortingTypeNames,
  FAVORITE_LOTS_PAGE,
  MY_LOTS_PAGE,
} from '../constants';
import LotService from '../services/LotService';
import SubscriptionService from '../services/SubscriptionService';
import { Filters } from './types/filters';
import { SearchLotParams } from './types/search';
import { createSearchParams, NavigateFunction } from 'react-router-dom';
import { RootStore } from './RootStore';
import { Tag } from './types/tag';

export class LotStore {
  rootstore: RootStore;
  lotService: InstanceType<typeof LotService>;
  subscriptionService: InstanceType<typeof SubscriptionService>;
  currentPage = 1;
  lotData: { id: string; isFavorited: boolean }[] = [];
  currentLotId = 0;
  searchLotData = [];
  lotDataTotal = 0;
  isFavorited: boolean;
  description = '';
  status = 'initial';
  searchQuery = '';
  isUserCharity: boolean;
  currentCategory = 0;
  query = '';
  searchInDescription = false;
  city = '';
  country = '';
  region = '';
  priceFrom = '';
  priceTo = '';
  lotState = '';
  locationIds: string[] = [];
  error: any;
  isLotDataLoading = false;
  isNewest = true;
  isOldest = false;
  isMostCheap = false;
  isMostExpensive = false;
  lotsPerPage = lotsPerPage;

  tagId: Tag = {
    id: null,
    name: '',
  };

  defaultFilters: Partial<Filters> = {
    query: '',
    locationIds: [],
    searchInDescription: false,
    searchInFrendlyLots: false,
    freezedLots: false,
    categoryId: 0,
    productType: '',
    sortingFlag: sortingTypeNames.isNewest,
    itemsPerPage: 24,
    pageNumber: 1,
    currency: '',
    lotType: '',
    lotState: null,
    tagIds: [],
  };

  filters = this.defaultFilters;
  toastIsActive = false;

  constructor(rootstore: RootStore) {
    this.lotService = new LotService();
    this.subscriptionService = new SubscriptionService();
    this.rootstore = rootstore;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setFilters = (newValues: Partial<Filters>) => {
    runInAction(() => {
      const newFilters = { ...this.filters, ...newValues };

      if (JSON.stringify(newFilters) !== JSON.stringify(this.filters)) {
        this.filters = newFilters;
      }
    });
  };

  setTagData = (newValue: number | null, name: string) => {
    this.tagId.id = newValue;
    this.tagId.name = name;
  };

  setCategory = (categoryId: number) => {
    this.currentCategory = categoryId;
    this.filters.categoryId = categoryId;
  };

  changeSearchURL = (location: Location) => {
    const filters = { ...this.filters };

    if (location.pathname === '/favorites') {
      delete filters.categoryId;
      this.isFavorited = true;
    } else if (location.pathname === '/myCharityLots') {
      filters.categoryId = BNS3_CHARITY_LOT_CATEGORY_ID;
      this.setFilters({
        categoryId: BNS3_CHARITY_LOT_CATEGORY_ID,
      });
      this.isUserCharity = true;
    } else {
      this.isFavorited = false;
      this.isUserCharity = false;

      if (filters.categoryId === 0) {
        delete filters.categoryId;
      }
    }
    if (filters.locationIds?.length === 0) {
      delete filters.locationIds;
    }

    const urlParams = [];

    const setDefault = <T extends keyof Filters>(key: T) => {
      filters[key] = this.defaultFilters[key];
    };

    Object.keys(filters).forEach(k => {
      const key = k as keyof Filters;
      if (filters[key] === 'undefined') {
        setDefault(key);
      }
      if (key === 'locationIds') {
        const locationIDsArr = filters[key] as Filters['locationIds'];
        locationIDsArr?.forEach(location => urlParams.push(`${key}=${location}`));
      } else if (filters[key] && (filters[key]?.toString().length || 0) > 0) {
        urlParams.push(`${key}=${filters[key]}`);
      }
    });
  };

  filterLots = (params: typeof MY_LOTS_PAGE.filterParam | typeof FAVORITE_LOTS_PAGE.filterParam) => {
    const filtersToRequest = { ...this.filters };

    if (this.isFavorited) {
      filtersToRequest.isFavorited = true;
    }

    if (this.isUserCharity) {
      filtersToRequest.createdByUser = true;
    }

    if (Number(filtersToRequest.categoryId) === 0 || !filtersToRequest.categoryId) {
      delete filtersToRequest.categoryId;
    }

    if (filtersToRequest.locationIds && !filtersToRequest.locationIds.length) {
      delete filtersToRequest.locationIds;
    }

    if (typeof filtersToRequest.locationIds === 'string') {
      filtersToRequest.locationIds = (filtersToRequest.locationIds as string).split(',');
    }

    if (filtersToRequest.lotType === 'all') {
      filtersToRequest.lotType = '';
    }

    if (filtersToRequest.lotType === '' && filtersToRequest.isFavorited) {
      delete filtersToRequest.isFavorited;
    }

    if (!filtersToRequest.currency) {
      if (!this.rootstore.userStore.currency) {
        return;
      }
      filtersToRequest.currency = this.rootstore.userStore.currency;
    }

    if (Array.isArray(filtersToRequest.productType)) {
      [filtersToRequest.productType, filtersToRequest.lotState] = filtersToRequest.productType;
    }

    if (this.tagId.id) {
      filtersToRequest.tagIds = [this.tagId.id];
    }

    if (filtersToRequest.productType === 'all') {
      filtersToRequest.productType = '';
      filtersToRequest.lotState = null;
    }
    this.searchLotsAsync({ ...filtersToRequest, ...params });
  };

  // TODO: do we need it ?
  // @ts-ignore
  updateFilterParams = params => {
    runInAction(() => {
      this.filters = {};
      this.query = params.query;
      this.searchInDescription = params.searchInDescription;
      this.city = params.city;
      this.country = params.country;
      this.region = params.region;
      this.priceFrom = params.priceFrom;
      this.priceTo = params.priceTo;
      this.lotState = params.lotState;
      this.rootstore.locationStore.updateFilterParamLocation(params);
      this.locationIds = params.locationIds === '' ? [] : params.locationIds;
      this.setTagData(null, '');
    });
  };

  resetFiltersParams = () => {
    this.filters = this.defaultFilters;
    this.deleteFilterTags();
  };

  updateFilterParamLocations = (locationId: string) => {
    runInAction(() => {
      const idx = this.locationIds.indexOf(locationId);
      this.locationIds.splice(idx, 1);
    });
  };

  resetFilterParamLocationIds = () => {
    runInAction(() => {
      this.filters.locationIds = [];
    });
  };

  // TODO: do we need it?
  updateFilterTags = (tagId: number | null, name: string) => {
    runInAction(() => {
      this.setTagData(tagId, name);
      this.filters.pageNumber = 1;
    });
  };

  deleteFilterTags = () => {
    runInAction(() => {
      this.setTagData(null, '');
      delete this.filters.tagIds;
    });
  };

  setDefaultPage = () => {
    runInAction(() => {
      this.filters.pageNumber = 1;
      this.currentPage = 1;
      this.filters.itemsPerPage = 24;
    });
  };

  // TODO: do we need it?
  clearFilterAttr = <K extends keyof this, V extends this[K]>(key: K, value: V) => {
    runInAction(() => {
      this[key] = value;
      this.setTagData(null, '');
    });
  };

  updateCurrentPage(pageNumber: number) {
    this.currentPage = pageNumber;
  }

  increaseFavotiteLotTotalOn = (value: number) => {
    if (window.location.pathname === FAVORITE_LOTS_PAGE.href) {
      this.lotDataTotal += value;
    }
  };

  setLotsPerPage = (newValue: number) => {
    runInAction(() => {
      this.setFilters({
        itemsPerPage: newValue,
        pageNumber: 1,
      });

      this.lotsPerPage = newValue;
      this.updateCurrentPage(1);
    });
  };

  setCurrentLotId = (lotId: number, navigate: NavigateFunction) => {
    runInAction(() => {
      this.currentLotId = lotId;
      navigate(`/edit/${lotId}`);
    });
  };

  setCurrentCategory = (categoryId: number) => {
    runInAction(() => {
      this.setCategory(categoryId);
    });
  };

  getCurrentCategory = () => this.currentCategory;

  fetchLotsData = async ({
    pathname,
    query,
    locIds,
  }: { pathname?: string; query?: string; locIds?: string[] } = {}) => {
    const params: Partial<Filters & { priceFrom: string; priceTo: string; sfreezedLots: boolean }> = {};

    if (pathname) {
      const regex = /^\/lot\/\d+\/*\S*$/gi;
      const found = pathname.match(regex);
      if (found && found.length > 0) {
        return {};
      }
      this.rootstore.singleLotStore.resetSingleLots();
    } else {
      this.rootstore.singleLotStore.resetSingleLots();
    }
    switch (pathname) {
      case '/favorites':
        runInAction(() => {
          this.currentCategory = 0;
        });

        return this.searchLotsAsync({
          ...params,
          isFavorited: true,
          pageNumber: this.currentPage,
          itemsPerPage: this.lotsPerPage,
          currency: this.rootstore.userStore.currency,
        });

      case '/mylots':
        runInAction(() => {
          this.currentCategory = 0;
        });
        if (this.query) params.query = this.query;
        if (this.priceFrom) params.priceFrom = this.priceFrom;
        if (this.priceTo) params.priceTo = this.priceTo;
        if (this.lotState) params.lotState = this.lotState;

        return this.searchLotsAsync({
          ...params,
          createdByUser: true,
          pageNumber: this.currentPage,
          itemsPerPage: this.lotsPerPage,
          currency: this.rootstore.userStore.currency,
        });

      case '/myCharitylots':
        runInAction(() => {
          this.currentCategory = 0;
        });
        if (query) params.query = query;
        if (locIds) params.locationIds = locIds;

        return this.searchLotsAsync({
          ...params,
          categoryId: charityBannerCategoryId,
          createdByUser: true,
          IsAllImagesReturned: true,
          pageNumber: this.currentPage,
          itemsPerPage: this.lotsPerPage,
        });

      case '/charityList':
        if (query) params.query = query;
        if (locIds) params.locationIds = locIds;

        return this.searchLotsAsync({
          ...params,
          categoryId: charityBannerCategoryId,
          IsAllImagesReturned: true,
          pageNumber: this.currentPage,
          itemsPerPage: this.lotsPerPage,
        });

      default:
        if (this.filters.query) params.query = this.filters.query;
        if (this.filters.searchInDescription) params.searchInDescription = this.filters.searchInDescription;
        if (this.filters.categoryId && this.filters.categoryId !== 0) params.categoryId = this.filters.categoryId;
        if (this.filters.searchInFrendlyLots) params.searchInFrendlyLots = this.filters.searchInFrendlyLots;
        if (this.filters.freezedLots) params.sfreezedLots = this.filters.freezedLots;
        if (this.priceFrom) params.priceFrom = this.priceFrom;
        if (this.priceTo) params.priceTo = this.priceTo;
        if (this.lotState) params.lotState = this.lotState;
        if (this.locationIds) params.locationIds = this.rootstore.locationStore.locationIds;
        if (this.tagId.id) params.tagIds = [this.tagId.id];

        return this.searchLotsAsync({
          ...params,
          itemsPerPage: this.lotsPerPage,
          pageNumber: this.currentPage,
          currency: this.rootstore.userStore.currency,
        });
    }
  };

  getAllLotsAsync = async () => {
    try {
      runInAction(() => {
        // This showSpinner function does nothing
        this.rootstore.spinnerStore.showSpinner();
      });

      const itemsPerPage = new URLSearchParams(Object.entries({ itemsPerPage: lotsPerPage.toString() }));
      const response = await this.lotService.getAllLots(itemsPerPage);
      runInAction(() => {
        this.lotData = response.data.pageContent;
        this.lotDataTotal = response.data.totalItems;
      });
    } catch (error) {
      runInAction(() => {
        this.status = 'error';
      });
    } finally {
      runInAction(() => this.rootstore.spinnerStore.hideSpinner());
    }
  };

  searchLotsAsync = async (params: SearchLotParams) => {
    try {
      runInAction(() => {
        this.isLotDataLoading = true;
        this.rootstore.spinnerStore.showSpinner();
      });

      const query = queryString.stringify(params);
      const urlParams = new URLSearchParams(query);
      const response = await this.lotService.searchLots(urlParams);
      runInAction(() => {
        this.lotData = response.data.pageContent;
        this.lotDataTotal = response.data.totalItems;
      });
    } catch (error) {
      runInAction(() => {
        this.status = 'error';
      });
    } finally {
      runInAction(() => {
        this.isLotDataLoading = false;
        this.rootstore.spinnerStore.hideSpinner();
      });
    }
  };

  createLotAsync = async (model: FormData, navigate: NavigateFunction) => {
    // check which url should be redirected once a new lot is made.
    // at the moment mylots is default. Now also checking if new lot was made from the charity page
    const url = '/mylots';
    runInAction(() => this.rootstore.spinnerStore.showSpinner());
    try {
      const response = await this.lotService.createLot(model);
      if (response && response.status === 200) {
        toast.success('Lot was successfully created');
        navigate(url);
        runInAction(() => {
          this.status = 'success';
        });
      }
    } catch (error: any) {
      this.error = error;
      if (error && error.status === 401) {
        runInAction(() => this.rootstore.authStore.logoutUser());
        navigate('/');
        toast.error('You are not authorized, please login');
      } else {
        const errors = error.data.errors || error.data.Errors;
        Object.keys(errors).forEach(errorType => {
          errors[errorType].forEach((errorText: string) => toast.error(errorText));
        });
      }
      runInAction(() => {
        this.status = 'error';
      });
    } finally {
      runInAction(() => this.rootstore.spinnerStore.hideSpinner());
    }
  };

  updateLotAsync = async (lotId: string, Lot: FormData, navigate: NavigateFunction) => {
    runInAction(() => this.rootstore.spinnerStore.showSpinner());
    try {
      const response = await this.lotService.editLot(lotId, Lot);
      if (response.status === 200) {
        toast.success('Lot was successfully updated');
        if (this.rootstore.userStore.isAdmin) {
          navigate(`/lot/${lotId}`);
        } else {
          navigate('/mylots');
        }

        runInAction(() => {
          this.status = 'success';
        });
      }
    } catch (error: any) {
      this.error = error;
      if (error.response.status && error.response.status === 401) {
        runInAction(() => this.rootstore.authStore.logoutUser());
        navigate('/');
        toast.error('You are not authorized, please login');
      } else {
        const { errors } = error.response.data;
        let message = '';
        Object.entries(errors).forEach(([, errorMessage]) => {
          message += errorMessage;
        });

        toast.error(message);

        if (error.response.status && error.response.status === 400) {
          navigate('/');
        }
      }
      runInAction(() => {
        this.status = 'error';
      });
    } finally {
      runInAction(() => this.rootstore.spinnerStore.hideSpinner());
    }
  };

  deleteLotAsync = async (lotId: string, navigate: NavigateFunction) => {
    runInAction(() => this.rootstore.spinnerStore.showSpinner());
    try {
      const response = await this.lotService.delete(lotId);
      if (response && response.status === 200) {
        toast.info('Lot was deleted');
        const wasLastLotInPage = this.lotData.length === 1;
        navigate({
          pathname: '/mylots',
          search: createSearchParams({
            pageNumber:
              wasLastLotInPage && this.currentPage > 1
                ? (this.currentPage - 1).toString()
                : this.currentPage.toString(),
          }).toString(),
        });

        runInAction(() => {
          this.status = 'success';
        });
      }
    } catch (error: any) {
      this.error = error;
      const message = error?.data?.title || 'failed to delete lot';

      toast.error(message);
      runInAction(() => {
        this.status = 'error';
      });
    } finally {
      runInAction(() => this.rootstore.spinnerStore.hideSpinner());
    }
  };

  updateLocationsIds = (locationsIds: string[]) => {
    runInAction(() => {
      this.locationIds = locationsIds;
    });
  };

  changeLocationsIds = (locationId: string) => {
    runInAction(() => {
      if (this.locationIds === undefined) this.locationIds = [];

      if (!this.locationIds.includes(locationId)) {
        this.locationIds.push(locationId);
      } else {
        const idx = this.locationIds.indexOf(locationId);
        this.locationIds.splice(idx, 1);
      }
    });
  };

  sendSubscription = async (queryParams: ParsedQuery<string>) => {
    if (queryParams.searchInDescription && (!queryParams.query || !(queryParams.query as string).trim())) {
      toast.error('Search value should be specified');
      return;
    }
    runInAction(() => this.rootstore.spinnerStore.showSpinner());
    try {
      const response = await this.subscriptionService.postSubscription(prepareSubscriptionRequest(queryParams));
      if (response && response.status === 200) {
        this.toastIsActive = true;
        runInAction(() => {
          toast.success('Your Subscription is saved successfully!');
        });
      }
    } catch (error: any) {
      runInAction(() => {
        this.status = 'error';
      });

      const errorMessage = error.response?.data?.errors?.[0];

      if (errorMessage) {
        toast.error(errorMessage);
      } else {
        toast.error(!queryParams.query ? 'Fill search field' : 'Failed to subscribe');
      }
    } finally {
      runInAction(() => this.rootstore.spinnerStore.hideSpinner());
    }
  };

  dismissToast = () => {
    if (this.toastIsActive) {
      toast.dismiss();
      this.toastIsActive = false;
    }
  };

  removeLotFromFavorite = (lotId: string) => {
    this.lotData = this.lotData.filter(({ id }) => id !== lotId);
  };

  updateIsFavoritedLotValue = (lotId: string) => {
    this.lotData = this.lotData.map((item: { id: string; isFavorited: boolean }) => ({
      ...item,
      ...(item.id === lotId ? { isFavorited: !item.isFavorited } : {}),
    }));
  };

  addToFavorite = async (lotId: string) => {
    try {
      await this.lotService.addToFavorite(lotId);
      toast.dismiss();
      toast.success("The Lot was successfully added to 'My Favorites' list");
      runInAction(() => this.updateIsFavoritedLotValue(lotId));
    } catch (error: any) {
      runInAction(() => {
        this.status = 'error';
      });
      if (error.response && error.response.data) {
        toast.error(error.response.data);
      }
    } finally {
      runInAction(() => this.rootstore.spinnerStore.hideSpinner());
    }
  };

  removeFromFavorite = async (lotId: string, isFavoritePage: boolean) => {
    try {
      await this.lotService.removeFromFavorite(lotId);
      runInAction(() => {
        if (isFavoritePage) {
          this.removeLotFromFavorite(lotId);
        } else {
          this.updateIsFavoritedLotValue(lotId);
        }
      });
    } catch (error: any) {
      runInAction(() => {
        this.status = 'error';
      });
      if (error.response && error.response.data) {
        toast.error(error.response.data);
      }
    } finally {
      runInAction(() => this.rootstore.spinnerStore.hideSpinner());
    }
  };
}
